export interface PublicationLocationState {
  publicationReturnTo?: string;
  publicationReturnLabel?: string;
  publicationOpenedFromIndex?: boolean;
  publicationOpenedFromContext?: boolean;
}

function isSafeAppPath(value: unknown): value is string {
  return typeof value === 'string'
    && /^\/[a-z0-9][a-z0-9/_-]*(?:\?[^#]*)?$/i.test(value)
    && !value.startsWith('//');
}

export function contextualPublicationState(
  returnTo: string,
  returnLabel: string,
): PublicationLocationState {
  return {
    publicationReturnTo: returnTo,
    publicationReturnLabel: returnLabel,
    publicationOpenedFromContext: true,
  };
}

const PUBLICATION_SOURCE_PREFIX = 'publication-source:';

export function rememberPublicationSource(path: string, focusId?: string) {
  if (!isSafeAppPath(path)) return;
  sessionStorage.setItem(`${PUBLICATION_SOURCE_PREFIX}${path}`, JSON.stringify({
    scrollY: window.scrollY,
    focusId,
  }));
}

export function takeRememberedPublicationSource(path: string, focusPrefix?: string) {
  const key = `${PUBLICATION_SOURCE_PREFIX}${path}`;
  const raw = sessionStorage.getItem(key);
  if (!raw) return undefined;
  try {
    const value = JSON.parse(raw) as { scrollY?: unknown; focusId?: unknown };
    const remembered = {
      scrollY: typeof value.scrollY === 'number' && Number.isFinite(value.scrollY) ? value.scrollY : 0,
      focusId: typeof value.focusId === 'string' ? value.focusId : undefined,
    };
    if (focusPrefix && !remembered.focusId?.startsWith(focusPrefix)) return undefined;
    sessionStorage.removeItem(key);
    return remembered;
  } catch {
    sessionStorage.removeItem(key);
    return undefined;
  }
}

export function publicationReturnTarget(
  state: unknown,
  indexPath: '/knowledge' | '/blog',
): { path: string; label?: string; useHistory: boolean } {
  const value = state as PublicationLocationState | null;
  const requested = value?.publicationReturnTo;
  const fromIndex = requested === indexPath || requested?.startsWith(`${indexPath}?`);
  const fromContext = value?.publicationOpenedFromContext === true && isSafeAppPath(requested);
  if (!requested || (!fromIndex && !fromContext)) return { path: indexPath, useHistory: false };
  return {
    path: requested,
    label: value?.publicationReturnLabel,
    useHistory: value?.publicationOpenedFromIndex === true || fromContext,
  };
}

export function continuePublicationState(
  state: unknown,
  returnTo: string,
): PublicationLocationState {
  const value = state as PublicationLocationState | null;
  return {
    publicationReturnTo: returnTo,
    publicationReturnLabel: value?.publicationReturnLabel,
    publicationOpenedFromIndex: value?.publicationOpenedFromIndex,
    publicationOpenedFromContext: value?.publicationOpenedFromContext,
  };
}
